import { useEffect, useMemo, useRef, useState } from "react";
import {
  appScopedPublicKeyFromSecretKeyB64,
  encryptAppSecretWithAppKey,
  sealToKeyGrant,
} from "@tensilelabs/auth-client";
import { useAppVault } from "@tensilelabs/auth-client/react";
import { SecretVaultField } from "./SecretVaultField";
import {
  beginOAuth,
  clearConnectorCookies,
  configureConnector,
  connectorVncUrl,
  endConnectorSession,
  handbackConnectorSession,
  launchConnectorSession,
  putSecret,
  registerVaultPublicKey,
  requestCatalogEntry,
  startWhatsAppPairing,
  completeWhatsAppPairing,
  connectorGrantOffer,
  deleteWhatsAppSession,
  revokeConnectorGrant,
  storeConnectorGrant,
  streamWhatsAppPairing,
  type WhatsAppPairing,
  type ConfigSession,
} from "../../api";
import type { CatalogEntry, ConnectorBubble } from "../../lib/types";

type ModalState = "idle" | "working" | "error" | "done";

export function ConnectorManagerModal({
  entry,
  accountId,
  bubble,
  onClose,
  onChanged,
}: {
  entry: CatalogEntry;
  accountId: string;
  bubble?: ConnectorBubble;
  onClose: () => void;
  onChanged: () => void;
}) {
  const ui = entry.management_ui;
  const vault = useAppVault("taskjinn");
  const [state, setState] = useState<ModalState>("idle");
  const [message, setMessage] = useState<string | null>(null);

  const finish = (text: string) => {
    setState("done");
    setMessage(text);
    onChanged();
  };

  const run = async (action: () => Promise<void>) => {
    setState("working");
    setMessage(null);
    try {
      await action();
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "Something went wrong");
    }
  };

  /** Registers the public half of the unlocked app key so the OAuth callback can seal. */
  const ensureVaultKey = async (): Promise<string> => {
    const secretKey = vault.appScopedSecretKeyB64;
    if (!secretKey) throw new Error("Unlock your vault first.");
    const publicKey = await appScopedPublicKeyFromSecretKeyB64(secretKey);
    await registerVaultPublicKey(accountId, "taskjinn", publicKey);
    return secretKey;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Manage ${entry.display_name}`}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-[var(--tj-bg)] p-4 shadow-xl sm:rounded-2xl">
        <header className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{entry.display_name}</h2>
            <p className="text-xs text-[var(--tj-muted)]">
              {entry.kind} · {entry.category}
              {bubble ? ` · ${bubble.state.replace("_", " ")}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-[var(--tj-border)] px-2 py-1 text-xs"
          >
            Close
          </button>
        </header>

        {!ui ? (
          <InertPlaceholder text="This integration has no declarative management surface. It is managed by hand." />
        ) : ui.archetype === "oauth_hub" ? (
          <OauthHub
            entry={entry}
            ui={ui}
            onAuthorize={(scopes) =>
              run(async () => {
                const secretKey = vault.appScopedSecretKeyB64;
                if (!secretKey) throw new Error("Unlock your vault first.");
                await ensureVaultKey();
                const { authorize_url } = await beginOAuth({
                  accountId,
                  entryId: entry.entry_id,
                  scopes,
                });
                window.location.assign(authorize_url);
              })
            }
          />
        ) : ui.archetype === "device_pairing" ? (
          <DevicePairing
            ui={ui}
            accountId={accountId}
            entryId={entry.entry_id}
            requestable={bubble?.requestable ?? false}
            run={run}
            onDone={finish}
            vaultUnlocked={vault.unlocked}
            ensureVaultKey={ensureVaultKey}
            onRequest={() =>
              run(async () => {
                await requestCatalogEntry(accountId, entry.entry_id);
                finish("Requested — we'll notify you when it is available.");
              })
            }
          />
        ) : ui.archetype === "vault_form" ? (
          <VaultForm
            ui={ui}
            vaultUnlocked={vault.unlocked}
            onUnlockHint={() => setMessage("Unlock your vault to add credentials.")}
            onSubmit={(values) =>
              run(async () => {
                const secretKey = await ensureVaultKey();
                for (const field of ui.fields ?? []) {
                  const value = values[field.name];
                  if (!value) continue;
                  const envelope = await encryptAppSecretWithAppKey({
                    appScopedSecretKeyB64: secretKey,
                    accountId,
                    appId: "taskjinn",
                    secretId: field.name,
                    label: `${entry.display_name} · ${field.label}`,
                    category: "agent_dynamic",
                    isAgentDynamic: false,
                    plaintext: value,
                  });
                  await putSecret(accountId, envelope);
                }
                await configureConnector(accountId, entry.entry_id);
                finish("Credentials sealed and stored.");
              })
            }
          />
        ) : ui.archetype === "webhook_receiver" ? (
          <WebhookReceiver
            ui={ui}
            onMarkConfigured={() =>
              run(async () => {
                await configureConnector(accountId, entry.entry_id);
                finish("Marked configured.");
              })
            }
          />
        ) : ui.archetype === "web_session" ? (
          <WebSession
            entry={entry}
            accountId={accountId}
            vaultUnlocked={vault.unlocked}
            ensureVaultKey={ensureVaultKey}
            onSubmit={(action) => run(action)}
            onDone={finish}
          />
        ) : (
          <InertPlaceholder text="Unsupported management component." />
        )}

        {entry.allowed_domains && entry.allowed_domains.length > 0 ? (
          <section aria-label="Allowed domains" className="mt-4 text-xs text-[var(--tj-muted)]">
            <p className="font-medium text-[var(--tj-text)]">Verified outbound hosts</p>
            <ul className="mt-1 flex flex-wrap gap-1">
              {entry.allowed_domains.map((domain) => (
                <li
                  key={domain}
                  className="rounded-full border border-[var(--tj-border)] px-2 py-0.5"
                >
                  {domain}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {message ? (
          <p
            role={state === "error" ? "alert" : "status"}
            className="mt-3 text-sm text-[var(--tj-muted)]"
          >
            {message}
          </p>
        ) : null}
        {state === "working" ? (
          <p role="status" className="mt-3 text-sm text-[var(--tj-muted)]">
            Working…
          </p>
        ) : null}
      </div>
    </div>
  );
}

function InertPlaceholder({ text }: { text: string }) {
  return (
    <p className="rounded-lg border border-dashed border-[var(--tj-border)] p-3 text-sm text-[var(--tj-muted)]">
      {text}
    </p>
  );
}

function OauthHub({
  entry,
  ui,
  onAuthorize,
}: {
  entry: CatalogEntry;
  ui: Extract<NonNullable<CatalogEntry["management_ui"]>, { archetype: "oauth_hub" }>;
  onAuthorize: (scopes: string[]) => void;
}) {
  const [scopes, setScopes] = useState<string[]>(
    (ui.scopes_matrix ?? []).filter((scope) => scope.recommended).map((scope) => scope.id),
  );
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--tj-muted)]">
        Authorize {entry.display_name} with incremental scope grants. Only the selected scopes
        are requested.
      </p>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Scopes</legend>
        {(ui.scopes_matrix ?? []).map((scope) => (
          <label key={scope.id} className="flex items-start gap-2 rounded-lg border border-[var(--tj-border)] p-2 text-sm">
            <input
              type="checkbox"
              checked={scopes.includes(scope.id)}
              onChange={(event) =>
                setScopes((current) =>
                  event.target.checked
                    ? [...current, scope.id]
                    : current.filter((id) => id !== scope.id),
                )
              }
            />
            <span>
              <span className="font-medium">{scope.label}</span>
              {scope.description ? (
                <span className="block text-xs text-[var(--tj-muted)]">{scope.description}</span>
              ) : null}
            </span>
          </label>
        ))}
      </fieldset>
      <button
        type="button"
        onClick={() => onAuthorize(scopes)}
        className="inline-block rounded-lg border border-[var(--tj-border)] px-3 py-2 text-sm"
      >
        Authorize with {ui.auth_provider}
      </button>
      <p className="text-xs text-[var(--tj-muted)]">
        The bubble flips green when the provider callback records the connection.
      </p>
    </div>
  );
}

function DevicePairing({
  ui,
  accountId,
  entryId,
  requestable,
  run,
  onDone,
  onRequest,
  vaultUnlocked,
  ensureVaultKey,
}: {
  ui: Extract<NonNullable<CatalogEntry["management_ui"]>, { archetype: "device_pairing" }>;
  accountId: string;
  entryId: string;
  requestable: boolean;
  run: (action: () => Promise<void>) => void;
  onDone: (message: string) => void;
  onRequest: () => void;
  vaultUnlocked: boolean;
  ensureVaultKey: () => Promise<string>;
}) {
  const [pairing, setPairing] = useState<WhatsAppPairing | null>(null);
  const [allowBackground, setAllowBackground] = useState(false);

  useEffect(() => {
    const pairingId = pairing?.pairing_id;
    if (!pairingId || pairing?.state === "ready") return;
    const controller = new AbortController();
    void streamWhatsAppPairing({
      accountId,
      entryId,
      pairingId,
      declaredPairEndpoint: ui.pair_endpoint,
      signal: controller.signal,
      onEvent: (event, data) => {
        if (event === "qr" || event === "status" || event === "ready" || event === "error") {
          setPairing((current) => ({
            ...(current ?? { state: "starting" }),
            ...data,
          }) as WhatsAppPairing);
        }
      },
    }).catch(() => {
      // Aborted on unmount or a transient stream error; the status button still works.
    });
    return () => controller.abort();
  }, [accountId, entryId, ui.pair_endpoint, pairing?.pairing_id, pairing?.state]);

  if (requestable) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-[var(--tj-muted)]">
          This integration needs a pairing connector provisioned for your account before a QR
          code can be issued.
        </p>
        <button
          type="button"
          onClick={onRequest}
          className="rounded-lg border border-[var(--tj-border)] px-3 py-2 text-sm"
        >
          Request provisioning
        </button>
      </div>
    );
  }

  const paired = pairing?.state === "ready";
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--tj-muted)]">
        {ui.instructions || "Scan the pairing code with the companion app."}
      </p>
      <label className="flex items-start gap-2 text-xs text-[var(--tj-muted)]">
        <input
          type="checkbox"
          checked={allowBackground}
          disabled={!vaultUnlocked}
          onChange={(event) => setAllowBackground(event.target.checked)}
        />
        <span>
          Allow background restores for 12 hours. The app key is sealed to a TTL-bounded,
          in-memory grant so scheduled runs can reuse the session without a QR — revoke it
          with “Clear session”.
        </span>
      </label>
      {pairing?.qr_ascii || pairing?.qr ? (
        <pre
          aria-label="Pairing QR code"
          className="max-w-full overflow-x-auto rounded-lg border border-[var(--tj-border)] bg-white p-2 font-mono text-[5px] leading-[5px] text-black"
        >
          {pairing.qr_ascii ?? pairing.qr}
        </pre>
      ) : null}
      {pairing?.expires_at && !paired ? (
        <p className="text-xs text-[var(--tj-muted)]">
          Code expires at {new Date(pairing.expires_at).toLocaleTimeString()}.
        </p>
      ) : null}
      {pairing?.restored && !paired ? (
        <p className="text-xs text-[var(--tj-muted)]">
          Stored session restored; the connector is reconnecting without a new QR.
        </p>
      ) : null}
      {paired ? (
        <p role="status" className="text-sm">
          Device paired{pairing?.phone ? ` as ${pairing.phone}` : ""}. Save the session to finish.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {!paired ? (
          <button
            type="button"
            onClick={() =>
              run(async () => {
                const decryptSecretKeyB64 = vaultUnlocked
                  ? await ensureVaultKey()
                  : undefined;
                if (allowBackground && decryptSecretKeyB64) {
                  const offer = await connectorGrantOffer(accountId, entryId);
                  const envelope = await sealToKeyGrant(offer, decryptSecretKeyB64);
                  await storeConnectorGrant(accountId, entryId, envelope);
                }
                const next = await startWhatsAppPairing(
                  accountId,
                  entryId,
                  decryptSecretKeyB64,
                  ui.pair_endpoint,
                );
                setPairing(next);
              })
            }
            className="rounded-lg border border-[var(--tj-border)] px-3 py-2 text-sm"
          >
            {pairing ? "Restart pairing" : "Pair device"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() =>
              run(async () => {
                const result = await completeWhatsAppPairing(
                  accountId,
                  entryId,
                  ui.pair_endpoint,
                );
                setPairing(null);
                onDone(`Paired and session sealed (${result.archive_bytes} bytes).`);
              })
            }
            className="rounded-lg border border-[var(--tj-border)] px-3 py-2 text-sm"
          >
            Save session
          </button>
        )}
        <button
          type="button"
          onClick={() =>
            run(async () => {
              await revokeConnectorGrant(accountId, entryId);
              await deleteWhatsAppSession(accountId, entryId);
              setPairing(null);
              onDone("Stored WhatsApp session cleared.");
            })
          }
          className="rounded-lg border border-[var(--tj-border)] px-3 py-2 text-sm"
        >
          Clear session
        </button>
      </div>
      {pairing?.error ? (
        <p role="alert" className="text-sm text-[var(--tj-muted)]">
          {pairing.error}
        </p>
      ) : null}
      {pairing?.provider_available === false ? (
        <p role="alert" className="text-xs text-[var(--tj-muted)]">
          The connector is missing whatsapp-web.js; deploy the connector image with the provider
          installed.
        </p>
      ) : null}
      <p className="text-xs text-[var(--tj-muted)]">
        Pairing status streams from {ui.status_endpoint}.
      </p>
    </div>
  );
}

function VaultForm({
  ui,
  vaultUnlocked,
  onUnlockHint,
  onSubmit,
}: {
  ui: Extract<NonNullable<CatalogEntry["management_ui"]>, { archetype: "vault_form" }>;
  vaultUnlocked: boolean;
  onUnlockHint: () => void;
  onSubmit: (values: Record<string, string>) => void;
}) {
  const fields = useMemo(() => ui.fields ?? [], [ui.fields]);
  const [values, setValues] = useState<Record<string, string>>({});
  const missingRequired = fields.some((field) => field.required && !values[field.name]);
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      {fields.map((field) => (
        <SecretVaultField
          key={field.name}
          field={field}
          value={values[field.name] ?? ""}
          onChange={(value) => setValues((current) => ({ ...current, [field.name]: value }))}
        />
      ))}
      {!vaultUnlocked ? (
        <p role="note" className="text-xs text-[var(--tj-muted)]">
          Unlock your vault first — credentials are encrypted in the browser and never sent as
          plaintext.
        </p>
      ) : null}
      <button
        type="submit"
        disabled={missingRequired || !vaultUnlocked}
        onMouseDown={(event) => {
          if (!vaultUnlocked) {
            event.preventDefault();
            onUnlockHint();
          }
        }}
        className="rounded-lg border border-[var(--tj-border)] px-3 py-2 text-sm disabled:opacity-40"
      >
        Save credentials
      </button>
      {ui.test_endpoint ? (
        <p className="text-xs text-[var(--tj-muted)]">Verified via {ui.test_endpoint}.</p>
      ) : null}
    </form>
  );
}

function WebhookReceiver({
  ui,
  onMarkConfigured,
}: {
  ui: Extract<NonNullable<CatalogEntry["management_ui"]>, { archetype: "webhook_receiver" }>;
  onMarkConfigured: () => void;
}) {
  const url = typeof window === "undefined" ? ui.webhook_path : `${window.location.origin}${ui.webhook_path}`;
  const token = ui.verification_token_secret_ref ?? "not configured";
  return (
    <div className="space-y-3 text-sm">
      <label className="block space-y-1">
        <span className="text-xs text-[var(--tj-muted)]">Webhook URL</span>
        <input readOnly value={url} className="w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-surface)] px-3 py-2" />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-[var(--tj-muted)]">Verification token</span>
        <input readOnly value={`•••• ${token}`} className="w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-surface)] px-3 py-2" />
      </label>
      <button
        type="button"
        onClick={onMarkConfigured}
        className="rounded-lg border border-[var(--tj-border)] px-3 py-2"
      >
        Mark configured
      </button>
    </div>
  );
}

function WebSession({
  entry,
  accountId,
  vaultUnlocked,
  ensureVaultKey,
  onSubmit,
  onDone,
}: {
  entry: CatalogEntry;
  accountId: string;
  vaultUnlocked: boolean;
  ensureVaultKey: () => Promise<string>;
  onSubmit: (action: () => Promise<void>) => void;
  onDone: (message: string) => void;
}) {
  const [session, setSession] = useState<ConfigSession | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [instructions, setInstructions] = useState<string | null>(null);

  useEffect(() => {
    if (!session || !mountRef.current) return;
    let disposed = false;
    let rfb: { disconnect: () => void } | null = null;
    void (async () => {
      const { default: RFB } = await import("@novnc/novnc");
      if (disposed || !mountRef.current) return;
      rfb = new RFB(mountRef.current, connectorVncUrl(session, accountId), {
        credentials: { password: "" },
      });
    })();
    return () => {
      disposed = true;
      rfb?.disconnect();
    };
  }, [session, accountId]);

  return (
    <div className="space-y-3 text-sm">
      <p className="text-[var(--tj-muted)]">
        {entry.management_ui && "instructions" in entry.management_ui
          ? entry.management_ui.instructions
          : "Log in to the portal in the secure window, then hand the session back."}
      </p>
      {session ? (
        <div
          ref={mountRef}
          aria-label="Connector login session"
          className="h-64 w-full overflow-hidden rounded-lg border border-[var(--tj-border)] bg-black"
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!vaultUnlocked}
          onClick={() =>
            onSubmit(async () => {
              const secretKey = await ensureVaultKey();
              const publicKey = await appScopedPublicKeyFromSecretKeyB64(secretKey);
              const next = await launchConnectorSession({
                accountId,
                entryId: entry.entry_id,
                recipientPublicKeyB64: publicKey,
                decryptSecretKeyB64: secretKey,
              });
              setSession(next);
              setInstructions("Session open. Complete login and 2FA, then hand back.");
            })
          }
          className="rounded-lg border border-[var(--tj-border)] px-3 py-2 disabled:opacity-40"
        >
          Launch browser (VNC login)
        </button>
        <button
          type="button"
          disabled={!session}
          onClick={() =>
            onSubmit(async () => {
              if (!session) return;
              const result = await handbackConnectorSession({
                accountId,
                entryId: entry.entry_id,
                sessionId: session.task_id,
                token: session.session_token,
              });
              await endConnectorSession({
                accountId,
                entryId: entry.entry_id,
                sessionId: session.task_id,
                token: session.session_token,
              });
              setSession(null);
              onDone(`Session sealed (${result.cookies} cookies) and configured.`);
            })
          }
          className="rounded-lg border border-[var(--tj-border)] px-3 py-2 disabled:opacity-40"
        >
          Hand back to agent
        </button>
        <button
          type="button"
          onClick={() =>
            onSubmit(async () => {
              await clearConnectorCookies(accountId, entry.entry_id);
              onDone("Stored cookies cleared.");
            })
          }
          className="rounded-lg border border-[var(--tj-border)] px-3 py-2"
        >
          Clear stored cookies
        </button>
      </div>
      {instructions ? <p className="text-xs text-[var(--tj-muted)]">{instructions}</p> : null}
      {!vaultUnlocked ? (
        <p className="text-xs text-[var(--tj-muted)]">Unlock your vault to open the login window.</p>
      ) : null}
    </div>
  );
}
